import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { getSubscription } from 'redux/auto-topup/autoTopupActions'
import { AppState } from 'redux/config/store'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import CheckoutConfirmationSummary from './components/CheckoutConfirmationSummary'
import PaymentProcessingMessage from './components/PaymentProcessingMessage'

const POLL_INTERVAL_MS = 5000
const POLL_TIMEOUT_MS = 2 * 60 * 1000

const Container = styled.div`
  display: flex;
  justify-content: center;
  padding: 3rem 2rem;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 40rem;
  padding: 2rem;
  text-align: center;
`

const EmployeeMicromobilityCheckoutConfirmationView = React.memo(() => {
  const dispatch = useDispatch()
  const [timedOut, setTimedOut] = useState(false)
  const { subscriptions } = useSelector(
    (state: AppState) => state.autoTopup
  )
  const { currentBalance } = useSelector(
    (state: AppState) => state.employeePlatform.wallet
  )
  const subscription = subscriptions[0]
  const isPaymentProcessed = Boolean(
    subscription && currentBalance >= subscription.amount
  )
  const shouldPoll = !isPaymentProcessed && !timedOut

  const pollPaymentStatus = useCallback(() => {
    dispatch(getSubscription())
    dispatch(employeePlatformActions.getWalletBalance())
  }, [dispatch])

  useEffect(() => {
    if (!shouldPoll) return undefined

    let intervalId: NodeJS.Timeout
    let timeoutId: NodeJS.Timeout

    const stopPolling = () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }

    pollPaymentStatus()
    intervalId = setInterval(pollPaymentStatus, POLL_INTERVAL_MS)
    timeoutId = setTimeout(() => {
      stopPolling()
      setTimedOut(true)
    }, POLL_TIMEOUT_MS)

    return stopPolling
  }, [shouldPoll, pollPaymentStatus])

  return (
    <Container>
      {subscription ? (
        <CheckoutConfirmationSummary
          subscription={subscription}
          isPaymentProcessing={!isPaymentProcessed}
          paymentProcessingTimedOut={timedOut}
        />
      ) : (
        <EmptyState>
          <Text variant='h2'>Thank you for setting up your subscription!</Text>
          <Text variant='body1' textColor={palette.text.secondary}>
            We're loading your recurring payment details.
          </Text>
          <PaymentProcessingMessage timedOut={timedOut} />
        </EmptyState>
      )}
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  EmployeeMicromobilityCheckoutConfirmationView.displayName =
    'EmployeeMicromobilityCheckoutConfirmationView'
}

export default EmployeeMicromobilityCheckoutConfirmationView
